import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import ConfirmationModal from '../ui/ConfirmationModal';
import PromptModal from '../ui/PromptModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_TABS = [
  { id: 'product', label: 'Products', icon: '📦', emptyHint: 'Extract a product page to auto-save products here.' },
  { id: 'avatar',  label: 'Avatars',  icon: '👤', emptyHint: 'Extract an /about-us page to auto-save founder photos here.' },
  { id: 'logo',    label: 'Logos',    icon: '🏷️', emptyHint: 'Extract any homepage to auto-save its logo here.' },
];

const AssetLibraryPage = ({ onPickAsset }) => {
  const [activeKind, setActiveKind] = useState('product');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [renameModal, setRenameModal] = useState({ isOpen: false, asset: null, isProcessing: false });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, asset: null, isProcessing: false });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/asset-library`, {
        params: { kind: activeKind },
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      setItems(res.data?.items || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [activeKind]);

  useEffect(() => { load(); }, [load]);

  const counts = { product: 0, avatar: 0, logo: 0 };
  items.forEach(i => { if (counts[i.kind] !== undefined) counts[i.kind]++; });

  const currentTab = KIND_TABS.find(t => t.id === activeKind);

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="asset-library-page">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100">
          Asset Library
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Every product, founder photo, and logo you extract from a website is saved here automatically —
          reuse them across campaigns without re-extracting. This is <em>separate</em> from
          <strong> My Gallery</strong> (which holds ads you generate).
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200 dark:border-slate-700">
        {KIND_TABS.map(t => {
          const active = t.id === activeKind;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveKind(t.id)}
              data-testid={`asset-library-tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                active
                  ? 'border-fuchsia-500 text-fuchsia-700 dark:text-fuchsia-300'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              <span className="mr-1.5">{t.icon}</span>{t.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm" data-testid="asset-library-error">
          ⚠️ {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-slate-500 dark:text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl" data-testid="asset-library-empty">
          <div className="text-5xl mb-3">{currentTab?.icon}</div>
          <p className="text-slate-600 dark:text-slate-300 font-medium">No {currentTab?.label.toLowerCase()} yet</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{currentTab?.emptyHint}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {items.map(asset => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onPick={onPickAsset}
              onRename={() => setRenameModal({ isOpen: true, asset, isProcessing: false })}
              onDelete={() => setDeleteModal({ isOpen: true, asset, isProcessing: false })}
            />
          ))}
        </div>
      )}

      {/* Rename */}
      <PromptModal
        isOpen={renameModal.isOpen}
        onClose={() => renameModal.isProcessing ? null : setRenameModal({ isOpen: false, asset: null, isProcessing: false })}
        onSubmit={async (newName) => {
          const a = renameModal.asset;
          if (!a) return;
          setRenameModal(prev => ({ ...prev, isProcessing: true }));
          try {
            const token = localStorage.getItem('session_token');
            const res = await axios.patch(`${API}/asset-library/${a.id}`, { name: newName }, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            const saved = res.data?.asset;
            setItems(prev => prev.map(x => x.id === a.id ? { ...x, name: saved?.name || newName } : x));
            setRenameModal({ isOpen: false, asset: null, isProcessing: false });
          } catch (err) {
            setError(err.response?.data?.detail || err.message || 'Rename failed');
            setRenameModal(prev => ({ ...prev, isProcessing: false }));
          }
        }}
        title="Rename asset"
        message="Give it a memorable name so you can find it faster later."
        defaultValue={renameModal.asset?.name || ''}
        placeholder="e.g. Hero shot of green pack"
        type="info"
        icon="✎"
        maxLength={160}
        isProcessing={renameModal.isProcessing}
      />

      {/* Delete */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => deleteModal.isProcessing ? null : setDeleteModal({ isOpen: false, asset: null, isProcessing: false })}
        onConfirm={async () => {
          const a = deleteModal.asset;
          if (!a) return;
          setDeleteModal(prev => ({ ...prev, isProcessing: true }));
          try {
            const token = localStorage.getItem('session_token');
            await axios.delete(`${API}/asset-library/${a.id}`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            setItems(prev => prev.filter(x => x.id !== a.id));
            setDeleteModal({ isOpen: false, asset: null, isProcessing: false });
          } catch (err) {
            setError(err.response?.data?.detail || err.message || 'Delete failed');
            setDeleteModal(prev => ({ ...prev, isProcessing: false }));
          }
        }}
        title="Delete asset permanently?"
        message={deleteModal.asset
          ? `"${deleteModal.asset.name || deleteModal.asset.source_domain || 'This asset'}" will be permanently deleted and can't be recovered.`
          : ''}
        confirmText="Delete permanently"
        cancelText="Keep"
        type="danger"
        isProcessing={deleteModal.isProcessing}
      />
    </div>
  );
};

const AssetCard = ({ asset, onPick, onRename, onDelete }) => (
  <div
    className="group relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-shadow"
    data-testid={`asset-library-card-${asset.id.slice(0,8)}`}
  >
    <div className="aspect-square bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
      <img
        src={asset.s3_url}
        alt={asset.name || asset.source_domain || 'asset'}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
    <div className="p-2.5">
      <p className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate" title={asset.name}>
        {asset.name || <span className="italic text-slate-400">Untitled</span>}
      </p>
      <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate" title={asset.source_url}>
        {asset.source_domain || '—'}
      </p>
    </div>
    {/* Hover actions */}
    <div className="absolute inset-x-0 top-0 flex items-center justify-end gap-1 p-1.5 bg-gradient-to-b from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
      {onPick && (
        <button
          type="button"
          onClick={() => onPick(asset)}
          data-testid={`asset-library-pick-${asset.id.slice(0,8)}`}
          className="text-[10px] font-semibold px-2 py-1 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white shadow"
        >
          Use
        </button>
      )}
      <button
        type="button"
        onClick={onRename}
        data-testid={`asset-library-rename-${asset.id.slice(0,8)}`}
        title="Rename"
        className="w-6 h-6 rounded-full bg-white/90 hover:bg-white text-slate-700 flex items-center justify-center text-xs shadow"
      >✎</button>
      <button
        type="button"
        onClick={onDelete}
        data-testid={`asset-library-remove-${asset.id.slice(0,8)}`}
        title="Remove from library"
        className="w-6 h-6 rounded-full bg-white/90 hover:bg-red-500 hover:text-white text-slate-700 flex items-center justify-center text-xs shadow"
      >✕</button>
    </div>
  </div>
);

export { AssetCard };
export default AssetLibraryPage;
